import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { TID } from "@/lib/testIds";
import { WATCH_IMAGES, CLUB_EDITIONS, SCENIC_EDITIONS, SCENES, LIFESTYLE } from "@/lib/watchImages";

const collections = [
  {
    slug: "launch",
    title: "Launch Editions",
    meta: "Four 1,500-piece variations",
    body:
      "The founding run, all in 18K gold. Four dial-and-case directions, 1,500 of each. The chapter from which the house begins.",
    tier: "Reserve · Championship",
    image: WATCH_IMAGES.midnightLinks.front,
    accent: "from-[#1F382A] to-[#0C0F0A]",
  },
  {
    slug: "club",
    title: "Club Editions",
    meta: "100-piece collector editions",
    body:
      "Stainless steel, commissioned one club at a time. 100 pieces per club, no re-runs. A members-only allocation.",
    tier: "Steel Club · Jewel",
    image: CLUB_EDITIONS[0].front,
    accent: "from-[#2A2D2B] to-[#0C0F0A]",
  },
  {
    slug: "scenic-dial",
    title: "Scenic Dial",
    meta: "Hand-painted landscape dials",
    body:
      "Painted miniatures of the courses that matter — same movement, same complications, in steel. Released as a limited capsule.",
    tier: "Scenic Dial · By allocation",
    image: SCENIC_EDITIONS[0].front,
    accent: "from-[#3a2f1a] to-[#0C0F0A]",
  },
  {
    slug: "scenic-petite",
    title: "Scenic Petite",
    meta: "28, 30, 32 mm — Women's line",
    body:
      "The Scenic Dial in three smaller proportions. Same painted dials, refined for jewelry-watch wear, no clutter on the face.",
    tier: "Club · Jewel · By allocation",
    image: SCENIC_EDITIONS[3].front,
    accent: "from-[#2c1f2a] to-[#0C0F0A]",
  },
];

export default function Collections() {
  return (
    <section
      id="collections"
      data-testid={TID.sectionCollections}
      className="relative py-28 sm:py-40 border-t border-[rgba(197,160,89,0.15)] overflow-hidden"
    >
      {/* Ambient editorial backdrop: white-glove Scenic Petite wrist shot — distinct from any other section */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src={SCENES.petiteWrist}
          alt=""
          className="w-full h-full object-cover object-[80%_center] opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#14201a] via-[#14201a]/60 to-[#14201a]/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#14201a]/70 via-transparent to-[#14201a]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3">
            <span className="gold-rule" />
            <span className="overline">The four lines</span>
          </div>
          <h2 className="font-serif-display text-[#F5F2EB] font-light text-balance text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-6">
            One watch.{" "}
            <span className="italic text-[#C5A059]">Four ways to wear it</span>.
          </h2>
          <p className="text-[#A8A9AD] text-base sm:text-lg leading-relaxed mt-8 max-w-xl">
            One movement. One house DNA. Four expressions — gold for
            the founding collector, steel for the club commission,
            hand-painted for the scenic capsule, and jewelry-scaled for
            the women&apos;s line.
          </p>
        </motion.div>

        <div className="mt-16 flex flex-wrap justify-center">
          {collections.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: "easeOut" }}
              className="w-full md:w-1/2 border border-[rgba(197,160,89,0.15)] -mt-px -ml-px box-border"
            >
              <Link
                to={`/collection/${c.slug}`}
                data-testid={TID.collectionCard(c.slug)}
                className="relative bg-[#0C0F0A] p-8 sm:p-10 group overflow-hidden block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059]"
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${c.accent} opacity-60 group-hover:opacity-80 transition-opacity duration-700`}
                />
                {c.image && (
                  <div className="absolute inset-0 opacity-25 group-hover:opacity-45 transition-opacity duration-700">
                    <img
                      src={c.image}
                      alt=""
                      className={`w-full h-full ${
                        c.imageFit === "cover" ? "object-cover" : "object-contain"
                      }`}
                    />
                  </div>
                )}
                <div className="relative">
                  <div className="flex items-start justify-between">
                    <div className="overline">{c.meta}</div>
                    <div className="font-serif-display text-[#C5A059] text-lg">
                      {c.tier}
                    </div>
                  </div>
                  <h3 className="mt-6 font-serif-display font-light text-3xl sm:text-4xl text-[#F5F2EB] group-hover:text-[#C5A059] transition-colors duration-500">
                    {c.title}
                  </h3>
                  <p className="mt-5 text-[#A8A9AD] leading-relaxed text-sm sm:text-base max-w-md">
                    {c.body}
                  </p>
                  <div className="mt-10 inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-[#C5A059]">
                    View the catalog
                    <span className="block w-8 h-px bg-[#C5A059] transition-all duration-500 group-hover:w-14" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

